import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Home.css";

// import hooks
import { useAuthContext } from "../../hooks/useAuthContext";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { useIsResTecnico } from "../../hooks/useIsResTecnico";
import { useCollection } from "../../hooks/useCollection";
import { useDocument } from "../../hooks/useDocument";

// import icone
// import date-fns
import {
  startOfMonth,
  endOfMonth,
  isWeekend,
  isFuture,
  format,
  isFriday,
  addDays,
  startOfDay,
  isSameDay,
} from "date-fns";
import { it } from "date-fns/locale";
import { timestamp } from "../../firebase/config";
import {
  ArrowLeft,
  Building2,
  BriefcaseBusiness,
  CalendarPlus,
  CalendarRange,
  ClipboardCheck,
  FileText,
  NotebookText,
  QrCode,
  Truck,
  Wallet,
} from "lucide-react";

// import componenti
import NavCard from "../../components/NavCard";
import Badge from "../badge/Badge";

const settoriLegacy = [
  { value: "Edile", label: "Settore Edile" },
  { value: "Cucine", label: "Settore Equipment Cucine" },
  { value: "Intesa", label: "Settore Banca Intesa" },
  { value: "Elettrico", label: "Settore Elettrico" },
  { value: "FTV", label: "Settore Fotovoltaico" },
  { value: "ROBY", label: "Personale Roby" },
  { value: "Amministrazione", label: "Amministrazione" },
  { value: "Cessati", label: "Ex Dipendenti" },
];

export default function Home() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const admin = useIsAdmin(user);
  const resTecnico = useIsResTecnico(user);
  const { document: userDoc } = useDocument("users", user.uid);
  const [giorniIncompleti, setGiorniIncompleti] = useState(0);
  const [mostraModalAzienda, setMostraModalAzienda] = useState(false);
  const [mostraModalBadge, setMostraModalBadge] = useState(false);
  const [mostraModalAltreAttivita, setMostraModalAltreAttivita] =
    useState(false);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 768px)").matches
      : false,
  );

  // Calcola le date
  const oggi = React.useMemo(() => new Date(), []);
  const firstDayOfMonth = React.useMemo(() => startOfMonth(oggi), [oggi]);
  const lastDayOfMonth = React.useMemo(() => endOfMonth(oggi), [oggi]);

  // Logica per il giorno successivo (Domani o Lunedì se oggi è Venerdì)
  const isTodayFriday = React.useMemo(() => isFriday(oggi), [oggi]);
  const domani = React.useMemo(() => addDays(oggi, 1), [oggi]);
  const lunedi = React.useMemo(() => addDays(oggi, 3), [oggi]);

  const oggiStr = React.useMemo(() => format(oggi, "yyyy-MM-dd"), [oggi]);
  const domaniStr = React.useMemo(() => format(domani, "yyyy-MM-dd"), [domani]);
  const lunediStr = React.useMemo(() => format(lunedi, "yyyy-MM-dd"), [lunedi]);

  const { documents: presenzeUtenteRaw } = useCollection("presenze", [
    ["userId", "==", user.uid],
  ]);

  const { documents: timbratureUtenteRaw } = useCollection("timbrature", [
    ["userId", "==", user.uid],
  ]);

  // Recupera gli avvisi attivi
  const { documents: avvisiAttivi } = useCollection("avvisi", [
    ["visibile", "==", true],
  ]);

  const { document: attivitaOggi } = useDocument(
    "attivita",
    `${user.uid}_${oggiStr}`,
  );

  // Recupera sempre l'attività di domani (Sabato se oggi è Venerdì)
  const { document: attivitaDomani } = useDocument(
    "attivita",
    `${user.uid}_${domaniStr}`,
  );

  // Recupera l'attività di Lunedì (solo se oggi è Venerdì, altrimenti usa un ID fittizio per evitare fetch inutili o errori)
  const { document: attivitaLunedi } = useDocument(
    "attivita",
    isTodayFriday ? `${user.uid}_${lunediStr}` : "dummy_id",
  );

  // Determina quale attività mostrare nella card "Domani"
  let nextActivity = attivitaDomani;
  let nextDate = domani;
  let nextTitle = "Domani";

  if (isTodayFriday) {
    // Se oggi è venerdì
    if (attivitaDomani) {
      // Se c'è attività sabato, mostra sabato
      nextActivity = attivitaDomani;
      nextDate = domani;
      nextTitle = "Sabato";
    } else {
      // Altrimenti mostra lunedì
      nextActivity = attivitaLunedi;
      nextDate = lunedi;
      nextTitle = "Lunedì";
    }
  }

  // Format date strings for display
  const oggiDisplay = format(oggi, "EEEE d MMMM", { locale: it });
  const nextDateDisplay = format(nextDate, "EEEE d MMMM", { locale: it });

  // Capitalize first letter
  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const oggiDisplayCap = capitalize(oggiDisplay);
  const nextDateDisplayCap = capitalize(nextDateDisplay);

  // Calcola la data di inizio per le attività future (giorno successivo a nextDate)
  const startFutureDate = React.useMemo(() => addDays(nextDate, 1), [nextDate]);
  const startFutureTimestamp = React.useMemo(
    () => timestamp.fromDate(startOfDay(startFutureDate)),
    [startFutureDate],
  );

  // Recupera le attività future
  const { documents: attivitaFuture } = useCollection(
    "attivita",
    [
      ["userId", "==", user.uid],
      ["date", ">=", startFutureTimestamp],
    ],
    ["date", "asc"],
  );

  // --- LOGICA NOTIFICHE RES TECNICO ---
  const shouldFetchResTecnicoData = resTecnico || admin;

  // Recupera tutti gli utenti (solo se necessario)
  const { documents: allUsers } = useCollection(
    shouldFetchResTecnicoData ? "users" : "users_dummy",
  );

  // Recupera ferie (solo se necessario) - Recuperiamo tutto e filtriamo in memoria per coerenza con altre pagine
  const { documents: allFerie } = useCollection(
    shouldFetchResTecnicoData ? "ferie" : "ferie_dummy",
  );

  // Recupera richieste info documenti (solo se necessario)
  const { documents: richiesteInfoDocs } = useCollection(
    shouldFetchResTecnicoData ? "richiesteInfo" : "richiesteInfo_dummy",
  );

  // Recupera tickets (solo se necessario)
  const { documents: ticketsDocs } = useCollection(
    shouldFetchResTecnicoData ? "tickets" : "tickets_dummy",
  );

  // Recupera settori (solo se necessario)
  const { documents: settoriDocs } = useCollection(
    shouldFetchResTecnicoData ? "settori" : "settori_dummy",
  );

  const pendingRequestsCount = React.useMemo(() => {
    if (!shouldFetchResTecnicoData || !allUsers || !allFerie) return 0;

    // 1. Filtra ferie pending
    const pendingFerie = allFerie.filter((req) => req.status === "pending");

    if (pendingFerie.length === 0) return 0;

    // 2. Trova il settore dell'utente corrente
    // Usa userDoc se disponibile, altrimenti cerca in allUsers
    let mySector = userDoc?.anagrafica?.settore;

    if (!mySector && allUsers) {
      const me = allUsers.find((u) => u.id === user.uid);
      mySector = me?.anagrafica?.settore;
    }

    // Normalizza il settore (trim)
    if (typeof mySector === "string") {
      mySector = mySector.trim();
    }

    // Se admin non ha settore, o se settore è ROBY, mostra tutto
    const isSuperSector = mySector === "ROBY";
    const isAdminNoSector = admin && !mySector;

    if (isSuperSector || isAdminNoSector) {
      return pendingFerie.length;
    }

    if (!mySector) return 0;

    // 3. Trova gli utenti del settore
    const sectorUserIds = new Set();
    allUsers.forEach((u) => {
      let uSector = u.anagrafica?.settore;
      if (typeof uSector === "string") {
        uSector = uSector.trim();
      }

      if (uSector === mySector) {
        sectorUserIds.add(u.id);
      }
    });

    // 4. Conta le richieste del settore
    const count = pendingFerie.filter((req) =>
      sectorUserIds.has(req.userId),
    ).length;

    return count;
  }, [shouldFetchResTecnicoData, allUsers, allFerie, user.uid, userDoc, admin]);

  const pendingRichiesteInfoCount = React.useMemo(() => {
    if (!shouldFetchResTecnicoData || !richiesteInfoDocs) return 0;

    let mySector = userDoc?.anagrafica?.settore;

    if (!mySector && allUsers) {
      const me = allUsers.find((u) => u.id === user.uid);
      mySector = me?.anagrafica?.settore;
    }

    if (typeof mySector === "string") {
      mySector = mySector.trim();
    }

    const isSuperSector = mySector === "ROBY";
    const isAdminNoSector = admin && !mySector;

    if (isSuperSector || isAdminNoSector) {
      return richiesteInfoDocs.filter((r) => {
        if (!r.settori || !Array.isArray(r.settori)) return false;
        return r.settori.some((s) => s.status === "pending");
      }).length;
    }

    if (!mySector) return 0;

    const mySectorNorm = mySector;

    const matchesSectorName = (nomeSettore) => {
      if (!nomeSettore) return false;
      let nome = nomeSettore;
      if (typeof nome === "string") {
        nome = nome.trim();
      }
      if (nome === mySectorNorm) return true;
      if (nome.includes(mySectorNorm)) return true;
      if (mySectorNorm.includes(nome)) return true;
      return false;
    };

    return richiesteInfoDocs.filter((r) => {
      if (!r.settori || !Array.isArray(r.settori)) return false;
      return r.settori.some(
        (s) => s.status === "pending" && matchesSectorName(s.nome),
      );
    }).length;
  }, [
    shouldFetchResTecnicoData,
    richiesteInfoDocs,
    allUsers,
    user.uid,
    userDoc,
    admin,
  ]);

  const pendingTicketsTalosCount = React.useMemo(() => {
    if (!shouldFetchResTecnicoData || !ticketsDocs) return 0;

    const assegnati = ticketsDocs.filter((t) => t.status === "assegnato");
    if (assegnati.length === 0) return 0;

    let mySector = userDoc?.anagrafica?.settore;
    if (!mySector && allUsers) {
      const me = allUsers.find((u) => u.id === user.uid);
      mySector = me?.anagrafica?.settore;
    }
    if (typeof mySector === "string") {
      mySector = mySector.trim();
    }

    const isSuperSector = mySector === "ROBY";
    const isAdminNoSector = admin && !mySector;

    const userSectorCode = mySector;
    const legacyObj = settoriLegacy.find((s) => s.value === userSectorCode);
    const userSectorLabel = legacyObj ? legacyObj.label : userSectorCode;

    let firestoreSectorId = null;
    if (settoriDocs) {
      const firestoreSector = settoriDocs.find(
        (s) => s.nome === userSectorLabel || s.nome === userSectorCode,
      );
      if (firestoreSector) {
        firestoreSectorId = firestoreSector.id;
      }
    }

    const filteredBySector = assegnati.filter((t) => {
      if (isSuperSector || isAdminNoSector) return true;
      if (!mySector) return false;

      if (
        firestoreSectorId &&
        (t.settoreId === firestoreSectorId ||
          t.settore?.id === firestoreSectorId)
      )
        return true;
      if (
        t.settoreNome === userSectorLabel ||
        t.settore?.nome === userSectorLabel
      )
        return true;
      if (
        t.settoreNome === userSectorCode ||
        t.settore?.nome === userSectorCode
      )
        return true;
      if (t.settoreId === userSectorCode || t.settore?.id === userSectorCode)
        return true;

      return false;
    });

    const count = filteredBySector.filter((t) => {
      if (!t.vistiRT || !Array.isArray(t.vistiRT)) return true;
      return !t.vistiRT.some((v) => v.userId === user.uid);
    }).length;

    return count;
  }, [
    shouldFetchResTecnicoData,
    ticketsDocs,
    allUsers,
    user.uid,
    userDoc,
    admin,
    settoriDocs,
  ]);

  // Calcola i giorni incompleti
  useEffect(() => {
    if (presenzeUtenteRaw) {
      // Filtra i giorni del mese escludendo weekend e date future
      const giorniDelMese = [];
      const currentDate = new Date(firstDayOfMonth);

      while (currentDate <= lastDayOfMonth) {
        // Aggiungi solo i giorni lavorativi (non weekend) e non futuri
        if (!isWeekend(currentDate) && !isFuture(currentDate)) {
          giorniDelMese.push(new Date(currentDate));
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }

      const toDateObject = (value) => {
        if (!value) return null;
        if (typeof value === "object" && typeof value.toDate === "function") {
          try {
            return value.toDate();
          } catch (_) {
            return null;
          }
        }
        if (value instanceof Date) return value;
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
      };

      const monthKey = format(oggi, "yyyy-MM");

      const presenzeDates = new Set(
        presenzeUtenteRaw
          .map((presenza) => {
            const dateObj = toDateObject(presenza?.dataRegistrazione);
            if (!dateObj) return null;
            if (format(dateObj, "yyyy-MM") !== monthKey) return null;
            return format(startOfDay(dateObj), "yyyy-MM-dd");
          })
          .filter(Boolean),
      );

      const timbratureDates = new Set(
        (timbratureUtenteRaw || [])
          .map((timbratura) => {
            const dateObj = toDateObject(timbratura?.dataRegistrazione);
            if (!dateObj) return null;
            if (format(dateObj, "yyyy-MM") !== monthKey) return null;
            return format(startOfDay(dateObj), "yyyy-MM-dd");
          })
          .filter(Boolean),
      );

      const giorniIncompletiCalcolati = giorniDelMese.filter((giorno) => {
        const isOggi = isSameDay(giorno, oggi);
        const isPassato = giorno < oggi;
        if (!isPassato && !isOggi) return false;

        const dataFormattata = format(giorno, "yyyy-MM-dd");
        const hasPresenzaObject = presenzeDates.has(dataFormattata);
        if (hasPresenzaObject) return false;

        const hasTimbraturaObject = timbratureDates.has(dataFormattata);
        return !hasPresenzaObject || hasTimbraturaObject;
      });

      // Imposta il numero di giorni incompleti
      setGiorniIncompleti(giorniIncompletiCalcolati.length);
    }
  }, [
    presenzeUtenteRaw,
    timbratureUtenteRaw,
    firstDayOfMonth,
    lastDayOfMonth,
    oggi,
  ]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const handleMediaChange = (event) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleMediaChange);
      return () => mediaQuery.removeEventListener("change", handleMediaChange);
    }

    mediaQuery.onchange = handleMediaChange;
    return () => {
      mediaQuery.onchange = null;
    };
  }, []);

  useEffect(() => {
    if (!isDesktop) {
      setMostraModalAltreAttivita(false);
    }
  }, [isDesktop]);

  // Funzione per chiudere i modali quando si clicca fuori
  const chiudiModalSeCliccaFuori = (e) => {
    if (e.target.classList.contains("modal-overlay")) {
      setMostraModalAzienda(false);
      setMostraModalBadge(false);
      setMostraModalAltreAttivita(false);
    }
  };

  // Card per lo stato delle presenze
  const statoPresenzeCard = {
    action: "foglioPresenza",
    nome:
      giorniIncompleti > 0
        ? "Compila il tuo Foglio Presenza"
        : "Foglio Presenza Completo",
    message:
      giorniIncompleti > 0
        ? `Hai ${giorniIncompleti} ${
            giorniIncompleti === 1 ? "giorno" : "giorni"
          } ${
            giorniIncompleti === 1 ? "incompleto" : "incompleti"
          } nel mese corrente.`
        : "Complimenti! Hai compilato tutti i giorni del mese corrente.",
    tipo: giorniIncompleti > 0 ? "warning" : "success",
    Icon: ClipboardCheck,
  };

  const elementi = [
    {
      action: "documenti",
      nome: "Documenti personali",
      message: "Scarica i tuoi documenti personali",
      Icon: FileText,
    },
    {
      action: "richiesta-ferie",
      nome: "Richiesta Ferie",
      message: "Invia una richiesta di ferie",
      Icon: CalendarPlus,
    },
    {
      action: "spesa",
      nome: "Aggiungi Spesa",
      message: "Inserisci una spesa per conto di Giuvi",
      Icon: Wallet,
    },
  ];

  const timbratoreElement = {
    action: "timbratore",
    nome: "Timbratore",
    message: "Scansiona il QR per entrare o uscire",
    Icon: QrCode,
  };

  // Elemento visibile solo ai resTecnico e admin
  const resTecnicoElement = {
    action: "restecnico",
    nome: "Area Responsabile Tecnico",
    message: "Strumenti per i Responsabili Tecnici",
    tipo: "restecnico",
    notificationCount: pendingRequestsCount,
    secondaryNotificationCount: pendingRichiesteInfoCount,
    orangeNotificationCount: pendingTicketsTalosCount,
    Icon: BriefcaseBusiness,
  };

  const avvisiFiltrati = avvisiAttivi
    ? avvisiAttivi.filter(
        (avviso) =>
          !avviso.destinatari ||
          avviso.destinatari.length === 0 ||
          avviso.destinatari.includes(user.uid),
      )
    : [];

  const quickLinkCards = [statoPresenzeCard, ...elementi];
  const futureActivityCards = (attivitaFuture || []).map((attivita) => ({
    id: attivita.id,
    dateKey: format(attivita.date.toDate(), "yyyy-MM-dd"),
    title: capitalize(format(attivita.date.toDate(), "EEEE", { locale: it })),
    dateLabel: capitalize(
      format(attivita.date.toDate(), "EEEE d MMMM", { locale: it }),
    ),
    activity: attivita,
    cardClassName: "attivita-future",
    headingTag: "h4",
  }));
  const activityCards = [
    {
      id: "today",
      dateKey: oggiStr,
      title: "Attività di Oggi",
      dateLabel: oggiDisplayCap,
      activity: attivitaOggi,
      cardClassName: "attivita-today",
      headingTag: "h3",
    },
    {
      id: "next",
      dateKey: format(nextDate, "yyyy-MM-dd"),
      title: nextTitle,
      dateLabel: nextDateDisplayCap,
      activity: nextActivity,
      cardClassName: "attivita-tomorrow",
      headingTag: "h4",
    },
    ...futureActivityCards,
  ];
  const visibleActivityCards = isDesktop
    ? activityCards.slice(0, 3)
    : activityCards;
  const remainingDesktopActivityCards = isDesktop ? activityCards.slice(3) : [];
  const openDayPage = (dateKey) => {
    if (!dateKey) return;
    navigate(`/giornata/${dateKey}`);
  };
  const renderActivityCard = (card) => {
    const HeadingTag = card.headingTag;

    return (
      <div
        key={card.id}
        className={`attivita-card attivita-card-clickable ${card.cardClassName}`}
        role="link"
        tabIndex={0}
        onClick={() => openDayPage(card.dateKey)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openDayPage(card.dateKey);
          }
        }}
      >
        <div className="attivita-card-header">
          <HeadingTag>
            <CalendarRange size={16} className="attivita-card-icon" />
            {card.title}
          </HeadingTag>
          <h5 className="date-subtitle">{card.dateLabel}</h5>
        </div>
        {card.activity ? (
          <div className="attivita-details">
            <div className="attivita-row">
              <span className="label">
                <Building2 size={14} className="attivita-row-icon" />
                Cantiere
              </span>
              <span className="value">
                {card.activity.project?.nome || "—"}
              </span>
            </div>
            <div className="attivita-row">
              <span className="label">
                <Truck size={14} className="attivita-row-icon" />
                Mezzo
              </span>
              <span className="value">
                {card.activity.mezzo?.modello || "—"}
              </span>
            </div>
            {card.activity.note && (
              <div className="attivita-note">
                <NotebookText size={14} className="attivita-row-icon" />
                <span>{card.activity.note}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="attivita-empty">Nessuna attività pianificata</div>
        )}
      </div>
    );
  };

  return (
    <div className="home-page">
      <div className="home-top-actions">
        <button
          className="info-azienda-btn"
          onClick={() => setMostraModalAzienda(true)}
        >
          Info Azienda
        </button>
        <button
          className="info-azienda-btn"
          onClick={() => setMostraModalBadge(true)}
        >
          Badge
        </button>
      </div>

      {/* Modal per i dati aziendali */}
      {mostraModalAzienda && (
        <div className="modal-overlay" onClick={chiudiModalSeCliccaFuori}>
          <div className="modal-content modal-content-azienda">
            <h2>Informazioni Aziendali</h2>
            <div className="info-azienda-content">
              <p>
                <strong>Nome Azienda:</strong> Giuvi S.r.l.
              </p>
              <p>
                <strong>Indirizzo:</strong> Via Belgirate, 15 - 20125 Milano
              </p>
              <p>
                <strong>P.IVA/C.Fisc:</strong> 07873720960
              </p>
              <p>
                <strong>Cod. SDI:</strong> W7YVJK9
              </p>
              <p>
                <strong>Telefono:</strong> +39 02 97 19 03 15
              </p>
              <p>
                <strong>Email:</strong> info@giuvi.it
              </p>
              <p>
                <strong>Sito Web:</strong> www.giuvi.it
              </p>
            </div>
            <button
              className="chiudi-modal-btn"
              onClick={() => setMostraModalAzienda(false)}
            >
              Chiudi
            </button>
          </div>
        </div>
      )}

      {mostraModalBadge && (
        <div className="modal-overlay" onClick={chiudiModalSeCliccaFuori}>
          <div className="modal-content modal-content-badge">
            <Badge />
            <button
              className="chiudi-modal-btn"
              onClick={() => setMostraModalBadge(false)}
            >
              Chiudi
            </button>
          </div>
        </div>
      )}

      {/* Sezione Avvisi */}
      {avvisiFiltrati.length > 0 && (
        <div className="avvisi-section">
          {avvisiFiltrati.map((avviso) => (
            <div
              key={avviso.id}
              className={`avviso-banner priority-${avviso.priorita}`}
            >
              <div className="avviso-icon">
                {avviso.priorita === "alta"
                  ? "🚨"
                  : avviso.priorita === "media"
                    ? "⚠️"
                    : "ℹ️"}
              </div>
              <div className="avviso-content">
                <h4>{avviso.titolo}</h4>
                <p>{avviso.contenuto}</p>
                <span className="avviso-date">
                  {avviso.createdAt &&
                    format(avviso.createdAt.toDate(), "d MMM yyyy", {
                      locale: it,
                    })}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="home-priority-grid">
        <div className="home-nav-item">
          <NavCard data={timbratoreElement} />
        </div>
        {(resTecnico || admin) && (
          <div className="home-nav-item">
            <NavCard data={resTecnicoElement} />
          </div>
        )}
      </div>

      <div className="home-section-divider" aria-hidden="true">
        <span>Attivita Pianificate</span>
      </div>

      <div className="attivita-click-hint">
        <span>Clicca su un giorno per aprire la giornata</span>
      </div>

      <div className="attivita-section">
        {visibleActivityCards.map(renderActivityCard)}
      </div>

      {!isDesktop && (
        <div className="attivita-mobile-hint">
          <ArrowLeft size={16} />
          <span>Swipa da destra per vedere gli altri giorni</span>
        </div>
      )}

      {isDesktop && remainingDesktopActivityCards.length > 0 && (
        <div className="attivita-more-actions">
          <button
            type="button"
            className="attivita-more-btn"
            onClick={() => setMostraModalAltreAttivita(true)}
          >
            Vedi altre attività ({remainingDesktopActivityCards.length})
          </button>
        </div>
      )}

      {mostraModalAltreAttivita && (
        <div className="modal-overlay" onClick={chiudiModalSeCliccaFuori}>
          <div className="modal-content attivita-more-modal">
            <h2>Altre Attività Programmate</h2>
            <div className="attivita-more-list">
              {remainingDesktopActivityCards.map(renderActivityCard)}
            </div>
            <button
              type="button"
              className="chiudi-modal-btn"
              onClick={() => setMostraModalAltreAttivita(false)}
            >
              Chiudi
            </button>
          </div>
        </div>
      )}

      <div className="home-section-divider" aria-hidden="true">
        <span>Link Rapidi</span>
      </div>

      <div className="home-nav-grid">
        {quickLinkCards.map((elem) => (
          <div key={elem.nome} className="home-nav-item">
            <NavCard data={elem} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * - pulsante per inserire Task --> FATTO
 * - pulsante per inserire Spesa --> FATTO
 * - pulsante per andare al proprio profilo --> FATTO
 */
